import Tippy from "@tippyjs/react"
import { Avatar } from "components/Avatar"
import {
  formatDate,
  formatDateForConversationMessage,
  removeTimestampInFileName,
} from "helpers/utils"
import { isDecryptionBlocked } from "helpers/decryptionBlocked"
import LockIcon from "assets/images/icons/icon-lock.svg"
import { cloneDeep, isUndefined } from "lodash"
import DotCard from "../atoms/DotCard"
import { ConversationMessagesDetailItem } from "../conversations.type"
import { ReactComponent as IconMessagerDeleteComponent } from "assets/images/icons/icon-messager-delete-component.svg"
import { ReactComponent as IconMessagerAddComponent } from "assets/images/icons/bell-icon.svg"
import { onRedirectConversationComponent } from "helpers/redirect"
import { downloadQueueManager } from "components/DownloadProcess/downloadQueue"
import { v4 as uuid } from "uuid"
import { useEffect, useMemo, useState } from "react"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { PuffLoader } from "react-spinners"
import { getAccessTokenInLocalStorage } from "helpers/access_token"
import {
  ApprovalStatus,
  MODEL_ACTIVITY_LOG,
} from "components/activity-log/activity-log.type"
import Button from "components/Button/Button"
import { ReactComponent as IconInfoCircle } from "assets/images/icons/icon-info-circle.svg"
import { TippyCustomzie } from "components/TippyCustomzie"
import { useBoolean } from "helpers/hooks"
import { postApprovalLogMiddleWare } from "../conversations.api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { getPendingApprovalsMiddleware } from "pages/layout/services/api"
import ActionItem from "pages/projects/molecules/ActionItem"
import { ReactComponent as SelectIcon } from "assets/images/icons/icon-arrow-down-select-tippy.svg"
import { runtimeKeyStore } from "stores/runtimeKeyStore"

interface Props {
  messages: ConversationMessagesDetailItem
  styleRoot?: React.CSSProperties
  idUserInfo: string
  idInviteeUser: string
  isActiveConversation?: boolean
  conversationId: string
  subNameDownload: string
  dbKey: string
  isOwner: boolean
  onReply?: (message: ConversationMessagesDetailItem) => void
  handlrScrollCommmentFrom?: (commentFromId: string) => void
}
const MessagesConversationItemCard = (props: Props) => {
  const {
    messages,
    styleRoot,
    isActiveConversation,
    conversationId,
    subNameDownload,
    dbKey,
    idUserInfo,
    isOwner,
    onReply,
    handlrScrollCommmentFrom,
  } = props
  const [processedContent, setProcessedContent] = useState(messages.content)
  const [isProcessing, setIsProcessing] = useState(false)
  const showInfoApprovalLog = useBoolean(false)
  const showMessageMenu = useBoolean(false)
  const [listApprovalLogs, setListApprovalLogs] = useState<
    {
      content: ApprovalStatus
      created_at: string
      user: {
        avatar: string
        id: string
        fullname: string
        email: string
      }
    }[]
  >([])

  useEffect(() => {
    if (!messages.approval_logs) return
    setListApprovalLogs(messages.approval_logs)
  }, [JSON.stringify(messages.approval_logs)])

  // Extract files from content based on file IDs
  const filesFromContent = useMemo(() => {
    if (!messages.content || !messages.files) return []

    // Extract file IDs from img tags in content
    const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g
    const fileIds: string[] = []
    let match

    while ((match = imgRegex.exec(messages.content)) !== null) {
      fileIds.push(match[1])
    }
    // Match file IDs with messages.files
    const matchedFiles = messages.files.filter((file) =>
      fileIds.includes(file.id)
    )

    return matchedFiles
  }, [messages.content, messages.files])

  // Process content to replace file IDs with base64 images
  useEffect(() => {
    const processContent = async () => {
      if (!filesFromContent.length || !messages.messageKey) {
        setProcessedContent(messages.content)
        return
      }
      setIsProcessing(true)
      try {
        const processedFiles = await Promise.all(
          filesFromContent.map(async (file) => {
            try {
              const base64Str =
                await encryptionGCMHelper.getFileImageFromEncrypted(
                  file.file,
                  messages.messageKey || "",
                  getAccessTokenInLocalStorage(),
                  {
                    conversation_id: conversationId,
                  },
                  "base64",
                  undefined,
                  dbKey
                )
              return {
                ...file,
                base64Str: base64Str as string,
              }
            } catch (error) {
              return {
                ...file,
                base64Str: "",
              }
            }
          })
        )

        // Replace file IDs in content with base64 strings
        let newContent = messages.content
        processedFiles.forEach((file) => {
          if (file.base64Str) {
            newContent = newContent.replace(file.id, file.base64Str)
          }
        })

        setProcessedContent(newContent)
      } catch (error) {
        setProcessedContent(messages.content)
      } finally {
        setIsProcessing(false)
      }
    }

    processContent()
  }, [JSON.stringify(filesFromContent)])

  const onClickBuildComponent = () => {
    if (
      !isUndefined(messages.is_public_to_conversation) &&
      Boolean(messages.is_public_to_conversation) &&
      messages.commented_from
    ) {
      onRedirectConversationComponent(
        messages.commented_from.project_component_type,
        messages.commented_from.project_component_id,
        messages.commented_from.project_build_component_id,
        messages.commented_from.project_component_history_id,
        conversationId,
        isActiveConversation,
        {
          idHistoryTreeState:
            messages.commented_from.project_component_history_id,
        },
        undefined,
        dbKey
      )
    }
  }

  const handleApprove = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: messages.id,
        model: MODEL_ACTIVITY_LOG.CONVERSATION_BUILDS,
        status: ApprovalStatus.APPROVED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      dbKey,
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Approved successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage
          messenger="This action is unavailable because the approval request has already been cancelled."
          type="error"
        />
      )
    }
  }
  const handleReject = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: messages.id,
        model: MODEL_ACTIVITY_LOG.CONVERSATION_BUILDS,
        status: ApprovalStatus.REJECTED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      dbKey,
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Rejected successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage
          messenger="This action is unavailable because the approval request has already been cancelled."
          type="error"
        />
      )
    }
  }
  const handleCancellation = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: messages.id,
        model: MODEL_ACTIVITY_LOG.CONVERSATION_BUILDS,
        status: ApprovalStatus.CANCELLED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      dbKey,
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Cancellation successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage messenger="Cancellation failed" type="error" />
      )
    }
  }

  const renderIconApproval = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#56A012" />
        <path
          d="M5 8L7 10L11 6"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  const renderIconRejected = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#EA4545" />
        <path
          d="M6 6L10 10M10 6L6 10"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  const renderIconPending = () => {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="8" cy="8" r="7" fill="#F7AC1B" />
        <path
          d="M8 4V8L10 10"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  const renderApprovalLogs = () => {
    if (!listApprovalLogs?.length) {
      return null
    }
    const required = listApprovalLogs.filter(
      (approvalLog) => approvalLog.content === ApprovalStatus.REQUIRED
    )
    const approved = listApprovalLogs.filter(
      (approvalLog) => approvalLog.content === ApprovalStatus.APPROVED
    )
    const rejected = listApprovalLogs.filter(
      (approvalLog) => approvalLog.content === ApprovalStatus.REJECTED
    )
    const cancelled = listApprovalLogs.filter(
      (approvalLog) => approvalLog.content === ApprovalStatus.CANCELLED
    )
    const totalRequired = required.length

    const approvedUserIds = approved.map((log) => log.user.id)
    const rejectedUserIds = rejected.map((log) => log.user.id)
    const pendingUsers = required.filter(
      (log) =>
        !approvedUserIds.includes(log.user.id) &&
        !rejectedUserIds.includes(log.user.id)
    )
    const pending = pendingUsers.length

    const isUserRequired =
      pendingUsers.some((pendingLog) => pendingLog.user.id === idUserInfo) &&
      !cancelled.length

    return (
      <div className="flex flex-col gap-[8px] mt-[8px] border-t border-[#E4E4E4] pt-[8px]">
        {required.length ? (
          <div className="flex items-center gap-[4px]">
            <div
              className="w-[22px] h-[22px] border border-[#e87a16] rounded-full"
              style={{
                backgroundColor: "rgba(232, 122, 22,0.5)",
              }}
            ></div>
            <p className="font-normal text-13 leading-22 color-black-111111 mr-1">
              {`Approval required: ${required
                .map(
                  (approvalLog) =>
                    `${approvalLog.user.fullname} - ${approvalLog.user.email}`
                )
                .join(", ")}`}
            </p>
          </div>
        ) : null}
        <div className="flex flex-col gap-[4px]">
          <div className="flex items-center gap-[12px]">
            <div className="flex items-center gap-[4px]">
              {renderIconApproval()}
              <p className="font-normal text-13 leading-22 color-black-111111">
                Approved: {approved.length}/{totalRequired}
              </p>
            </div>
            <div className="flex items-center gap-[4px]">
              {renderIconRejected()}
              <p className="font-normal text-13 leading-22 color-black-111111">
                Rejected: {rejected.length}/{totalRequired}
              </p>
            </div>
            <div className="flex items-center gap-[4px]">
              {renderIconPending()}
              <p className="font-normal text-13 leading-22 color-black-111111">
                Pending: {pending}/{totalRequired}
              </p>
            </div>
            <TippyCustomzie
              containerClass="component-card-menu"
              placement="top"
              interactive
              arrow={false}
              animation="scale"
              visible={showInfoApprovalLog.value}
              onClickOutside={() => showInfoApprovalLog.setValue(false)}
              content={
                <div
                  style={{
                    background: "#222222",
                    borderRadius: 4,
                    paddingTop: 8,
                    paddingBottom: 8,
                    minWidth: 200,
                    maxWidth: 300,
                  }}
                >
                  {required.map((el, index) => {
                    const approvedItem = approved.find(
                      (approvedEl) => approvedEl.user.id === el.user.id
                    )
                    const rejectedItem = rejected.find(
                      (rejectedEl) => rejectedEl.user.id === el.user.id
                    )
                    const pendingItem = pendingUsers.find(
                      (pendingEl) => pendingEl.user.id === el.user.id
                    )
                    return (
                      <div
                        key={index}
                        className="flex items-center gap-[4px] px-[12px] border-b"
                        style={{
                          borderColor:
                            index === required.length - 1
                              ? "transparent"
                              : "#333333",
                          paddingBottom:
                            index === required.length - 1 ? "0" : "8px",
                        }}
                      >
                        <div>
                          {approvedItem ? renderIconApproval() : null}
                          {rejectedItem ? renderIconRejected() : null}
                          {pendingItem ? renderIconPending() : null}
                        </div>
                        <div className="flex flex-col gap-[4px]">
                          <p className="font-normal text-13 leading-22 color-white">
                            {el.user.fullname} - {el.user.email}
                          </p>
                          {approvedItem || rejectedItem ? (
                            <p className="font-normal text-[10px] color-[#7A7A7A]">
                              {formatDate(
                                approvedItem?.created_at ||
                                  rejectedItem?.created_at ||
                                  "",
                                "DD MMM yyyy - HH:mm A"
                              )}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              }
              allowHTML
            >
              <div
                className="cursor-pointer"
                onClick={() => showInfoApprovalLog.setValue(true)}
              >
                <IconInfoCircle style={{ width: 22, height: 22 }} />
              </div>
            </TippyCustomzie>
          </div>
          {cancelled.length > 0 && (
            <div className="flex items-center gap-[4px] mt-[8px]">
              <span style={{ fontSize: 16 }}>🚫</span>
              <p
                className="font-bold text-13 leading-22"
                style={{
                  color: "#EA4545",
                }}
              >
                Approval cancelled by {cancelled[0].user.fullname} -{" "}
                {cancelled[0].user.email} at{" "}
                {formatDate(cancelled[0].created_at, "DD MMM yyyy - HH:mm A")}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-[8px] mt-[8px]">
          {isUserRequired ? (
            <>
              <Button
                title="Approve"
                sizeBtn="small"
                colorBtn="yellow"
                onClick={handleApprove}
                styleButton={{
                  width: 80,
                }}
              />
              <Button
                title="Reject"
                sizeBtn="small"
                colorBtn="red"
                onClick={handleReject}
                styleButton={{
                  width: 80,
                }}
              />
            </>
          ) : null}
          {(isOwner || idUserInfo === messages.created_by) &&
          !cancelled.length &&
          pendingUsers.length ? (
            <Button
              title="Cancellation"
              sizeBtn="small"
              colorBtn="red"
              onClick={handleCancellation}
              styleButton={{
                width: 80,
              }}
            />
          ) : null}
        </div>
      </div>
    )
  }
  if (
    !isUndefined(messages.is_notification) &&
    Boolean(messages.is_notification)
  ) {
    const isAdd = messages.content.includes("has been added")
    return (
      <div
        className="mr-6 md:mr-0 md:pr-6 flex items-center px-2 mb-3 last:mb-0"
        style={{
          minHeight: 38,
          background: isAdd
            ? "rgba(86, 160, 18, 0.15)"
            : "rgba(232, 122, 22,0.15)",
          borderRadius: 6,
        }}
      >
        {isAdd ? (
          <IconMessagerAddComponent
            className="icon-add-messager"
            style={{
              minWidth: 20,
            }}
          />
        ) : (
          <IconMessagerDeleteComponent
            className="icon-warning-messager"
            style={{
              minWidth: 20,
            }}
          />
        )}
        <p
          className="font-normal text-13 leading-22  pl-1"
          style={{
            color: isAdd ? "#56A012" : "#e87a16",
          }}
        >
          {messages.content}
        </p>
        {messages.created_at ? (
          <>
            <DotCard
              styleRoot={{
                backgroundColor: isAdd ? "#56A012" : "#e87a16",
              }}
            />
            <p
              className="text-12 leading-18 font-bold"
              style={{
                color: isAdd ? "#56A012" : "#e87a16",
              }}
            >
              {formatDateForConversationMessage(messages.created_at)}
            </p>
          </>
        ) : null}
      </div>
    )
  }
  const handleReply = () => {
    if (onReply) {
      onReply(messages)
    }
    showMessageMenu.setValue(false)
  }

  return (
    <div
      className="p-2 flex flex-col mb-3 cursor-pointer relative group"
      id={`msg-${messages.id}`}
      style={{
        backgroundColor: "#F7F7F7",
        borderRadius: 6,
        ...styleRoot,
      }}
    >
      {!isUndefined(messages.is_public_to_conversation) &&
      Boolean(messages.is_public_to_conversation) &&
      messages.commented_from ? (
        <div className="flex items-center mb-3">
          <p className="font-normal text-13 leading-22 color-black-111111 mr-1">
            Commented on
          </p>
          <p
            onClick={isActiveConversation ? onClickBuildComponent : undefined}
            className={`font-normal text-13 leading-22 ${
              isActiveConversation ? `hover:underline text-blue` : "text-black"
            }`}
          >
            {`${messages.commented_from.project_component_code} / ${messages.commented_from.project_component_history_code}`}
          </p>
        </div>
      ) : null}

      {/* Reply Bar - comment_from */}
      {messages.comment_from ? (
        <div
          style={{
            marginBottom: 8,
            marginRight: isActiveConversation && onReply ? "40px" : "0px",
          }}
        >
          <div
            onClick={() =>
              handlrScrollCommmentFrom?.(messages.comment_from?.id || "")
            }
            style={{
              background: "rgba(86, 160, 18, 0.15)",
              borderLeft: "4px solid rgb(86, 160, 18)",
              padding: "6px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p
                  className="font-bold text-13 leading-22"
                  style={{
                    color: "#111111",
                    marginBottom: 4,
                  }}
                >
                  {messages.comment_from.user.fullname} -{" "}
                  {messages.comment_from.user.email}
                </p>
                <p
                  className="font-normal text-12 leading-18"
                  style={{
                    color: "#111111",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {(() => {
                    const tmp = document.createElement("DIV")
                    tmp.innerHTML = messages.comment_from.content
                    return tmp.textContent || tmp.innerText || ""
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Message Menu Icon */}
      {isActiveConversation && onReply && (
        <div className="absolute top-2 right-2" style={{ zIndex: 10 }}>
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showMessageMenu.value}
            onClickOutside={() => showMessageMenu.setValue(false)}
            content={
              <div
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  width: 150,
                }}
              >
                <ActionItem
                  title="Reply"
                  onClick={handleReply}
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 4L6 8L10 12"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                />
              </div>
            }
            allowHTML
          >
            <div
              className="flex p-[6px] hover-action-icon cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                showMessageMenu.setValue(true)
              }}
            >
              <SelectIcon />
            </div>
          </TippyCustomzie>
        </div>
      )}

      <div className="flex items-center mb-2">
        <Avatar
          width="24px"
          height="24px"
          name={messages.user.fullname}
          avatar={messages.user.avatar}
          textcolor="white"
          contentsize="11px"
          justOneLetter={true}
        />
        <p
          className="font-semibold text-13 leading-22 color-black-111111 pl-2"
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {messages.user.fullname}
        </p>
        {messages.user.email ? (
          <div className="hidden md:flex md:items-center">
            <span className="pl-1"> - </span>
            <p
              className="font-semibold text-13 leading-22 color-black-111111 pl-1"
              style={{
                whiteSpace: "nowrap",
              }}
            >
              {messages.user.email}
            </p>
          </div>
        ) : null}
        <DotCard />
        <p className="text-12 leading-18 color-gray-7a font-normal">
          {messages.created_at
            ? `${formatDateForConversationMessage(messages.created_at)}`
            : "No update"}
        </p>
      </div>
      {isProcessing ? (
        <PuffLoader color="#F7AC1B" loading={true} size={30} />
      ) : isDecryptionBlocked(processedContent) ? (
        <div
          className="flex items-center gap-[6px]"
          style={{ color: "#9CA3AF" }}
        >
          <img
            src={LockIcon}
            alt="lock"
            style={{ width: 14, height: 14, opacity: 0.5 }}
          />
          <span style={{ fontSize: 13 }}>
            Content unavailable - encryption error
          </span>
        </div>
      ) : (
        <div
          style={{
            fontWeight: 400,
            fontSize: 13,
            lineHeight: "22px",
            color: "#111111",
          }}
          className="break-words message-item"
          dangerouslySetInnerHTML={{
            __html: processedContent,
          }}
        ></div>
      )}
      {messages.files && messages.files.length ? (
        <div className="flex items-center flex-wrap mt-2">
          {messages.files.map((file, index) => (
            <div
              key={index}
              onClick={() => {
                const conversationEncryptionKeys =
                  runtimeKeyStore.getConversationKeys()
                const conversationKey =
                  conversationEncryptionKeys[conversationId] || ""
                downloadQueueManager.add({
                  apiURL: file.file,
                  downloadId: uuid(),
                  name: removeTimestampInFileName(file.file_name),
                  subName: subNameDownload || "",
                  isFileOne: true,
                  dataFileOne: {
                    assetPath: file.file,
                    idConversationParam: conversationId,
                    decryptedKey: messages.messageKey || conversationKey,
                    dbKey,
                  },
                })
              }}
            >
              <Tippy
                className="custom-tippy-menu-left"
                placement="top"
                content={
                  <span
                    className="font-normal"
                    style={{
                      fontSize: 13,
                      lineHeight: "22px",
                      wordBreak: "break-word",
                    }}
                  >
                    {removeTimestampInFileName(file.file_name)}
                  </span>
                }
              >
                <div
                  className="flex items-center h-7 px-2 mr-2 mb-1 cursor-pointer"
                  style={{
                    background: "#F1F1F1",
                    border: "1px solid #E4E4E4",
                    borderRadius: 4,
                    maxWidth: 200,
                  }}
                >
                  <p
                    className="mr-1"
                    style={{
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "18px",
                      color: "#111111",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {removeTimestampInFileName(file.file_name)}
                  </p>
                </div>
              </Tippy>
            </div>
          ))}
        </div>
      ) : null}
      {renderApprovalLogs()}
    </div>
  )
}
export default MessagesConversationItemCard
